import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Clock,
  Phone,
  Mail,
  CheckCircle2,
  AlertTriangle,
  RefreshCw,
  Search,
  Copy,
  Check,
  Send,
  UserCheck,
  ArrowRight,
  Loader2,
  Calendar,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { formatPhoneDisplay, getPublicAppBaseUrl } from "@/lib/utils";
import AuthService from "@/lib/auth";

export interface QueueItem {
  id: string;
  token: string;
  first_name?: string | null;
  middle_name?: string | null;
  last_name?: string | null;
  derived_patient_name: string;
  phone: string | null;
  email: string | null;
  status: "pending" | "submitted" | "completed" | "expired" | "cancelled";
  expires_at: string | null;
  submitted_at: string | null;
  completed_at: string | null;
  created_at: string | null;
  patient_data: any;
  final_patient_id: string | null;
}

interface RegistrationQueueViewProps {
  onReviewAndComplete: (item: QueueItem) => void;
  onOpenSendLink: () => void;
}

export default function RegistrationQueueView({
  onReviewAndComplete,
  onOpenSendLink,
}: RegistrationQueueViewProps) {
  const [items, setItems] = useState<QueueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>("submitted");
  const [linksSubFilter, setLinksSubFilter] = useState<"all" | "active" | "expired">("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [resendingId, setResendingId] = useState<string | null>(null);

  // Badge counts
  const [submittedCount, setSubmittedCount] = useState(0);
  const [linksSentCount, setLinksSentCount] = useState(0);
  const [activePendingCount, setActivePendingCount] = useState(0);
  const [expiredCount, setExpiredCount] = useState(0);
  const [completedCount, setCompletedCount] = useState(0);
  const [allCount, setAllCount] = useState(0);

  // Re-generate Link Share Modal
  const [reshareModalData, setReshareModalData] = useState<{
    id: string;
    token: string;
    patientName: string;
    registration_url: string;
    expires_at: string;
    sms_sent: boolean;
    sms_error?: string | null;
    email_sent: boolean;
    email_error?: string | null;
  } | null>(null);
  const [reshareCopied, setReshareCopied] = useState(false);

  const fetchQueue = useCallback(async () => {
    try {
      const res: any = await AuthService.getRegistrationQueue(statusFilter, searchQuery);
      const itemsList =
        res?.items ??
        res?.data?.items ??
        (Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []));

      const subCount = res?.submitted_count ?? res?.data?.submitted_count ?? 0;
      const sentCount = res?.links_sent_count ?? res?.data?.links_sent_count ?? 0;
      const actCount = res?.active_pending_count ?? res?.data?.active_pending_count ?? 0;
      const expCount = res?.expired_count ?? res?.data?.expired_count ?? 0;
      const compCount = res?.completed_count ?? res?.data?.completed_count ?? 0;
      const totalAll = res?.all_count ?? res?.data?.all_count ?? (subCount + sentCount + compCount);

      setItems(itemsList);
      setSubmittedCount(subCount);
      setLinksSentCount(sentCount);
      setActivePendingCount(actCount);
      setExpiredCount(expCount);
      setCompletedCount(compCount);
      setAllCount(totalAll);
    } catch (err: any) {
      toast.error(err.message || "Failed to load registration queue");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [statusFilter, searchQuery]);

  useEffect(() => {
    setLoading(true);
    fetchQueue();
    // Auto-poll queue every 15 seconds
    const interval = setInterval(fetchQueue, 15000);
    return () => clearInterval(interval);
  }, [fetchQueue]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchQueue();
  };

  const handleCopyLink = (token: string, id: string) => {
    const origin = getPublicAppBaseUrl();
    const url = `${origin}/register/lead/${token}`;
    navigator.clipboard.writeText(url);
    setCopiedId(id);
    toast.success("Link copied to clipboard");
    setTimeout(() => setCopiedId(null), 2500);
  };

  const handleResendLink = async (item: QueueItem) => {
    try {
      setResendingId(item.id);
      const res: any = await AuthService.resendRegistrationLink(item.id, {
        first_name: item.first_name || undefined,
        middle_name: item.middle_name || undefined,
        last_name: item.last_name || undefined,
        phone: item.phone || undefined,
        email: item.email || undefined,
        send_sms: !!item.phone,
        send_email: !!item.email,
        preserve_data: true,
      });

      const d = res?.data || res;
      if (d && (d.registration_url || d.token)) {
        let finalUrl = d.registration_url;
        if (!finalUrl || finalUrl.includes("localhost") || finalUrl.includes("127.0.0.1")) {
          finalUrl = `${getPublicAppBaseUrl()}/register/lead/${d.token}`;
        }
        setReshareModalData({
          id: d.id || item.id,
          token: d.token,
          patientName: item.derived_patient_name || "Lead Patient",
          registration_url: finalUrl,
          expires_at: d.expires_at,
          sms_sent: !!d.sms_sent,
          sms_error: d.sms_error,
          email_sent: !!d.email_sent,
          email_error: d.email_error,
        });
        // Switch subfilter to "all" so the fresh active link is immediately visible in the table
        setLinksSubFilter("all");
        toast.success(`Fresh 10-minute registration link generated!`);
        fetchQueue();
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to resend link");
    } finally {
      setResendingId(null);
    }
  };

  const handleCopyReshare = () => {
    if (!reshareModalData?.registration_url) return;
    navigator.clipboard.writeText(reshareModalData.registration_url);
    setReshareCopied(true);
    toast.success("Registration link copied to clipboard");
    setTimeout(() => setReshareCopied(false), 2500);
  };

  // Synchronized counts for the current items in Links Sent tab
  const currentActiveCount = useMemo(() => {
    if (statusFilter === "links_sent") {
      return items.filter((i) => i.status === "pending").length;
    }
    return activePendingCount;
  }, [items, statusFilter, activePendingCount]);

  const currentExpiredCount = useMemo(() => {
    if (statusFilter === "links_sent") {
      return items.filter((i) => i.status === "expired").length;
    }
    return expiredCount;
  }, [items, statusFilter, expiredCount]);

  const currentSentCount = useMemo(() => {
    if (statusFilter === "links_sent") {
      return items.filter((i) => i.status === "pending" || i.status === "expired").length;
    }
    return linksSentCount;
  }, [items, statusFilter, linksSentCount]);

  // Sub-filter for Links Sent tab (Active vs Expired)
  const displayedItems = useMemo(() => {
    if (statusFilter !== "links_sent") return items;
    if (linksSubFilter === "active") return items.filter((i) => i.status === "pending");
    if (linksSubFilter === "expired") return items.filter((i) => i.status === "expired");
    return items;
  }, [items, statusFilter, linksSubFilter]);

  return (
    <div className="space-y-6">
      {/* Top Banner / Actions */}
      <div className="bg-white rounded-3xl p-6 sm:p-7 border border-gray-200 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight">
              Registration Queue
            </h2>
            {submittedCount > 0 && (
              <Badge className="bg-[#1a7a4a] text-white hover:bg-[#145d39] px-2.5 py-0.5 rounded-full text-xs font-semibold animate-pulse">
                {submittedCount} Ready for Review
              </Badge>
            )}
          </div>
          <p className="text-sm text-gray-500 mt-1">
            Track phone call leads, pending/expired 10-minute links, and finalize submitted profiles.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
            className="rounded-xl border-gray-200 text-gray-700 hover:text-[#1a7a4a] h-10 px-3.5"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin text-[#1a7a4a]" : ""}`} />
            Refresh
          </Button>

          <Button
            onClick={onOpenSendLink}
            className="bg-[#1a7a4a] hover:bg-[#145d39] text-white rounded-xl shadow-md h-10 px-4 font-semibold text-sm flex items-center gap-2"
          >
            <Send className="w-4 h-4" />
            Send New Link to Lead
          </Button>
        </div>
      </div>

      {/* Filter Tabs & Search */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-1.5 p-1 bg-gray-100 rounded-2xl w-fit flex-wrap">
          <button
            type="button"
            onClick={() => setStatusFilter("submitted")}
            className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold transition-all ${
              statusFilter === "submitted"
                ? "bg-white text-gray-900 shadow-sm font-bold"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            Ready for Review {submittedCount > 0 && `(${submittedCount})`}
          </button>

          <button
            type="button"
            onClick={() => {
              setStatusFilter("links_sent");
              setLinksSubFilter("all");
            }}
            className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold transition-all flex items-center gap-1.5 ${
              statusFilter === "links_sent"
                ? "bg-white text-emerald-800 shadow-sm font-bold"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            <span>Links Sent / Pending Leads</span>
            {linksSentCount > 0 && (
              <span className={`text-xs px-2 py-0.2 rounded-full font-bold ${
                statusFilter === "links_sent" ? "bg-emerald-100 text-emerald-800" : "bg-gray-200 text-gray-700"
              }`}>
                {linksSentCount}
              </span>
            )}
          </button>

          <button
            type="button"
            onClick={() => setStatusFilter("completed")}
            className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold transition-all flex items-center gap-1.5 ${
              statusFilter === "completed"
                ? "bg-white text-gray-900 shadow-sm font-bold"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            <span>Completed</span>
            {completedCount > 0 && (
              <span className={`text-xs px-2 py-0.2 rounded-full font-bold ${
                statusFilter === "completed" ? "bg-blue-100 text-blue-800" : "bg-gray-200 text-gray-700"
              }`}>
                {completedCount}
              </span>
            )}
          </button>

          <button
            type="button"
            onClick={() => setStatusFilter("all")}
            className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold transition-all flex items-center gap-1.5 ${
              statusFilter === "all"
                ? "bg-white text-gray-900 shadow-sm font-bold"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            <span>All History</span>
            {allCount > 0 && (
              <span className={`text-xs px-2 py-0.2 rounded-full font-bold ${
                statusFilter === "all" ? "bg-gray-200 text-gray-900" : "bg-gray-200 text-gray-600"
              }`}>
                {allCount}
              </span>
            )}
          </button>
        </div>

        <div className="relative max-w-xs w-full">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search patient, phone, email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 h-10 rounded-xl bg-white border-gray-200 text-sm"
          />
        </div>
      </div>

      {/* Sub-Filter Pills when viewing Sent Links */}
      {statusFilter === "links_sent" && (
        <div className="flex items-center gap-2 pt-1 pl-1 text-xs">
          <span className="text-gray-500 font-medium">Filter links:</span>
          <button
            type="button"
            onClick={() => setLinksSubFilter("all")}
            className={`px-3 py-1 rounded-lg font-semibold transition-all ${
              linksSubFilter === "all"
                ? "bg-gray-900 text-white"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            All Sent ({currentSentCount})
          </button>
          <button
            type="button"
            onClick={() => setLinksSubFilter("active")}
            className={`px-3 py-1 rounded-lg font-semibold transition-all flex items-center gap-1 ${
              linksSubFilter === "active"
                ? "bg-amber-600 text-white"
                : "bg-amber-50 text-amber-800 hover:bg-amber-100"
            }`}
          >
            <Clock className="w-3 h-3" />
            Active ({currentActiveCount})
          </button>
          <button
            type="button"
            onClick={() => setLinksSubFilter("expired")}
            className={`px-3 py-1 rounded-lg font-semibold transition-all flex items-center gap-1 ${
              linksSubFilter === "expired"
                ? "bg-rose-600 text-white"
                : "bg-rose-50 text-rose-800 hover:bg-rose-100"
            }`}
          >
            <AlertTriangle className="w-3 h-3" />
            Expired ({currentExpiredCount})
          </button>
        </div>
      )}

      {/* Queue Items */}
      {loading ? (
        <div className="bg-white rounded-3xl p-12 border border-gray-200 text-center space-y-3">
          <Loader2 className="w-8 h-8 animate-spin text-[#1a7a4a] mx-auto" />
          <p className="text-sm font-medium text-gray-600">Loading registration queue...</p>
        </div>
      ) : displayedItems.length === 0 ? (
        <div className="bg-white rounded-3xl p-12 border border-gray-200 text-center space-y-4">
          <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto text-gray-400">
            <CheckCircle2 className="w-8 h-8" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {statusFilter === "submitted"
                ? "Review Queue is Clear!"
                : statusFilter === "links_sent"
                ? "No Pending Lead Links Found"
                : "No Registrations Found"}
            </h3>
            <p className="text-sm text-gray-500 max-w-md mx-auto mt-1">
              {statusFilter === "submitted"
                ? "There are currently no patient submissions waiting for staff review. All incoming lead forms will appear here."
                : statusFilter === "links_sent"
                ? "No sent links matching this filter. Send a new 10-minute link to a lead during your phone call."
                : "No matching queue entries found for this filter."}
            </p>
          </div>
          <Button
            onClick={onOpenSendLink}
            variant="outline"
            className="rounded-xl border-gray-200 text-[#1a7a4a] hover:bg-green-50"
          >
            Send Link to a Lead
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {displayedItems.map((item) => {
            const isSubmitted = item.status === "submitted";
            const isPending = item.status === "pending";
            const isCompleted = item.status === "completed";
            const isExpired = item.status === "expired";

            return (
              <Card
                key={item.id}
                className={`bg-white border rounded-2xl transition-all shadow-sm overflow-hidden ${
                  isSubmitted
                    ? "border-green-200 hover:shadow-md hover:border-green-300"
                    : isExpired
                    ? "border-rose-200 bg-rose-50/20 hover:border-rose-300"
                    : "border-gray-200 hover:shadow-sm"
                }`}
              >
                <CardContent className="p-5 sm:p-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="space-y-2">
                    <div className="flex flex-wrap items-center gap-2.5">
                      <h4 className="text-base sm:text-lg font-bold text-gray-900">
                        {item.derived_patient_name}
                      </h4>

                      {/* Status Badges */}
                      {isSubmitted && (
                        <Badge className="bg-green-100 text-green-800 border-green-200 hover:bg-green-100 text-xs font-semibold px-2.5 py-0.5 rounded-full flex items-center gap-1">
                          <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />
                          Ready for Staff Review
                        </Badge>
                      )}
                      {isPending && (
                        <Badge className="bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-100 text-xs font-semibold px-2.5 py-0.5 rounded-full flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5 text-amber-600" />
                          Active Link &bull; Awaiting Lead
                        </Badge>
                      )}
                      {isCompleted && (
                        <Badge className="bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-100 text-xs font-semibold px-2.5 py-0.5 rounded-full flex items-center gap-1">
                          <UserCheck className="w-3.5 h-3.5 text-blue-600" />
                          Registered Patient
                        </Badge>
                      )}
                      {isExpired && (
                        <Badge className="bg-rose-100 text-rose-800 border-rose-200 hover:bg-rose-100 text-xs font-semibold px-2.5 py-0.5 rounded-full flex items-center gap-1">
                          <AlertTriangle className="w-3.5 h-3.5 text-rose-600" />
                          Link Expired (10m) &bull; Re-generate to Share
                        </Badge>
                      )}
                    </div>

                    {/* Contact & Timestamps */}
                    <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 text-xs text-gray-500">
                      {item.phone && (
                        <span className="flex items-center gap-1.5 font-medium text-gray-700">
                          <Phone className="w-3.5 h-3.5 text-gray-400" />
                          {formatPhoneDisplay(item.phone)}
                        </span>
                      )}
                      {item.email && (
                        <span className="flex items-center gap-1.5">
                          <Mail className="w-3.5 h-3.5 text-gray-400" />
                          {item.email}
                        </span>
                      )}
                      {item.submitted_at && (
                        <span className="flex items-center gap-1.5 text-[#1a7a4a] font-medium">
                          <Clock className="w-3.5 h-3.5" />
                          Submitted {formatDistanceToNow(new Date(item.submitted_at), { addSuffix: true })}
                        </span>
                      )}
                      {item.created_at && !item.submitted_at && (
                        <span className="flex items-center gap-1.5">
                          <Calendar className="w-3.5 h-3.5 text-gray-400" />
                          Sent {formatDistanceToNow(new Date(item.created_at), { addSuffix: true })}
                        </span>
                      )}
                    </div>

                    {/* Demographics Summary if Submitted */}
                    {isSubmitted && item.patient_data && (
                      <div className="pt-1 text-xs text-gray-600 flex flex-wrap gap-2">
                        {item.patient_data.date_of_birth && (
                          <span className="bg-gray-100 px-2 py-0.5 rounded-md">
                            DOB: {item.patient_data.date_of_birth}
                          </span>
                        )}
                        {item.patient_data.gender && (
                          <span className="bg-gray-100 px-2 py-0.5 rounded-md">
                            Gender: {item.patient_data.gender}
                          </span>
                        )}
                        {item.patient_data.city && item.patient_data.state && (
                          <span className="bg-gray-100 px-2 py-0.5 rounded-md">
                            Location: {item.patient_data.city}, {item.patient_data.state}
                          </span>
                        )}
                        <span className="bg-green-50 text-green-700 px-2 py-0.5 rounded-md font-medium">
                          Consents Signed: HIPAA &bull; eSign &bull; Remote Care{item.patient_data?.biometric_consent ? " • Biometrics" : ""}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-wrap items-center gap-2.5 pt-2 md:pt-0">
                    {/* Copy Link button (ONLY for active unexpired links) */}
                    {isPending && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleCopyLink(item.token, item.id)}
                        className="rounded-xl border-gray-200 text-gray-700 hover:text-gray-900 h-9 text-xs"
                      >
                        {copiedId === item.id ? (
                          <>
                            <Check className="w-3.5 h-3.5 mr-1 text-green-600" />
                            Copied!
                          </>
                        ) : (
                          <>
                            <Copy className="w-3.5 h-3.5 mr-1" />
                            Copy Link
                          </>
                        )}
                      </Button>
                    )}

                    {/* Re-generate & Share New Link button (PROMINENT for expired, also available for active) */}
                    {(isPending || isExpired) && (
                      <Button
                        variant={isExpired ? "default" : "outline"}
                        size="sm"
                        disabled={resendingId === item.id}
                        onClick={() => handleResendLink(item)}
                        className={`rounded-xl h-9 text-xs font-semibold flex items-center gap-1.5 shadow-sm ${
                          isExpired
                            ? "bg-[#1a7a4a] hover:bg-[#145d39] text-white"
                            : "border-gray-200 text-gray-700 hover:text-[#1a7a4a] hover:bg-green-50"
                        }`}
                      >
                        {resendingId === item.id ? (
                          <>
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <RefreshCw className="w-3.5 h-3.5" />
                            {isExpired ? "Re-generate & Share New Link" : "Re-generate Link"}
                          </>
                        )}
                      </Button>
                    )}

                    {/* Review & Complete (PRIMARY ACTION for submitted) */}
                    {isSubmitted && (
                      <Button
                        size="sm"
                        onClick={() => onReviewAndComplete(item)}
                        className="bg-[#1a7a4a] hover:bg-[#145d39] text-white rounded-xl shadow-md h-9 text-xs font-semibold px-4 flex items-center gap-1.5"
                      >
                        <Sparkles className="w-3.5 h-3.5" />
                        Review &amp; Complete Profile
                        <ArrowRight className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Fresh Link Dialog Modal (Opens immediately when staff clicks Re-generate) */}
      <Dialog open={!!reshareModalData} onOpenChange={(open) => !open && setReshareModalData(null)}>
        <DialogContent className="max-w-lg rounded-3xl p-6 sm:p-8 bg-white border border-gray-200 shadow-2xl">
          <DialogHeader className="space-y-2">
            <div className="flex items-center gap-2.5">
              <div className="w-10 h-10 rounded-2xl bg-green-100 flex items-center justify-center">
                <RefreshCw className="w-5 h-5 text-[#1a7a4a]" />
              </div>
              <div>
                <DialogTitle className="text-xl font-bold text-gray-900">
                  Fresh Link Generated &bull; Ready to Share
                </DialogTitle>
                <DialogDescription className="text-xs text-gray-500">
                  {reshareModalData?.patientName
                    ? `New 10-minute link for ${reshareModalData.patientName}`
                    : "New 10-minute lead registration link"}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="space-y-5 pt-2">
            <div className="bg-[#f0fdf4] border border-green-200 rounded-2xl p-4 space-y-2.5">
              <div className="flex items-center gap-2 text-green-800 font-bold text-sm">
                <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                <span>Link Active for 10 Minutes</span>
              </div>
              <p className="text-xs text-green-700 leading-relaxed">
                The lead can open this link to fill out their details and sign consents. Any previous expired link has been safely replaced.
              </p>
              <div className="flex flex-wrap gap-2 pt-1 text-xs">
                {reshareModalData?.sms_sent && (
                  <span className="bg-green-200/60 text-green-800 px-2 py-0.5 rounded-md font-medium">
                    ✓ SMS Delivered
                  </span>
                )}
                {reshareModalData?.email_sent && (
                  <span className="bg-green-200/60 text-green-800 px-2 py-0.5 rounded-md font-medium">
                    ✓ Email Delivered
                  </span>
                )}
                {reshareModalData?.email_error && (
                  <span className="bg-rose-100 text-rose-800 px-2 py-0.5 rounded-md font-medium">
                    ⚠ Email dispatch issue: {reshareModalData.email_error}
                  </span>
                )}
                {reshareModalData?.sms_error && (
                  <span className="bg-rose-100 text-rose-800 px-2 py-0.5 rounded-md font-medium">
                    ⚠ SMS dispatch issue: {reshareModalData.sms_error}
                  </span>
                )}
                <span className="bg-amber-100 text-amber-800 px-2 py-0.5 rounded-md font-medium flex items-center gap-1">
                  <Clock className="w-3 h-3" /> Valid for 10 minutes
                </span>
              </div>
            </div>

            {/* Direct URL Box */}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-gray-700">
                Direct Registration URL (Copy to Share)
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  readOnly
                  value={reshareModalData?.registration_url || ""}
                  className="h-11 bg-gray-50 text-xs font-mono rounded-xl truncate"
                />
                <Button
                  type="button"
                  onClick={handleCopyReshare}
                  className="h-11 px-4 bg-gray-900 hover:bg-gray-800 text-white rounded-xl text-xs flex-shrink-0 flex items-center gap-1.5"
                >
                  {reshareCopied ? (
                    <>
                      <Check className="w-4 h-4 text-green-400" />
                      <span>Copied!</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      <span>Copy</span>
                    </>
                  )}
                </Button>
              </div>
            </div>

            <div className="pt-2 flex justify-end">
              <Button
                type="button"
                onClick={() => setReshareModalData(null)}
                className="bg-[#1a7a4a] hover:bg-[#145d39] text-white rounded-xl text-xs font-semibold h-10 px-5 shadow-sm"
              >
                Done
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
